/**
 * Sales-dashboard join for the "a customer is waiting on you" alert card
 * (payment-gated confirmation slice 3, spec §3.12 items 4-5, founder
 * addition 2026-08-19). Turns the two pure predicates in `quoteFunnel.ts`
 * into rows `StaffDashboard.tsx` can render (pure builder, thin view).
 *
 * Reuses `isAcceptedAwaitingDeposit` / `hasRequestedUpdate` rather than
 * re-implementing them — a second copy of the `acceptedAt`/`updateRequestedAt`
 * check would be exactly the kind of drift docs/lessons.md Lesson #75 warns
 * against for a src/-side mirror. `now` is always injected — never
 * `Date.now()` inside — so callers own the clock and tests stay deterministic
 * (Lesson #49).
 */
import type { QuoteListLike } from '../services/quoteService';
import { displayQuoteRef } from '../../shared/quoteMessaging';
import { THIRTY_DAYS_MS, quotationDate } from '../../shared/quoteLifecycle';
import { isAcceptedAwaitingDeposit, hasRequestedUpdate } from './quoteFunnel';

const DAY_MS = 24 * 60 * 60 * 1000;

/** A quote can legitimately fire both predicates at once (accepted offline,
 *  then the customer also hit "request an updated quotation" — e.g. after
 *  the quote went on to expire). Rather than emit two rows for one quote,
 *  one row carries `BOTH` so the card can say so in a single line. */
export type AcceptedQuoteSignal = 'ACCEPTED' | 'UPDATE_REQUESTED' | 'BOTH';

export interface AcceptedQuoteRow {
  quoteId: string;
  /** Server-allocated `QTE-2026-NNNN` when present, else the legacy 8-char
   *  slice — via `displayQuoteRef`, never rebuilt inline (Lesson #33). */
  displayRef: string;
  customerName: string;
  grandTotal: number;
  signal: AcceptedQuoteSignal;
  /**
   * Days remaining to the 30-day expiry (positive), or days past it
   * (zero/negative) — one signed field serves both "to expiry" and "since
   * expiry" the way `QuotationsDashboard`'s own countdown/overdue math does.
   * Null when the quote carries no usable `sentAt` (legacy / never sent) —
   * real work, but no deadline to race, so it sorts last rather than first
   * or by an arbitrary tiebreak.
   */
  daysToExpiry: number | null;
}

/** Rows for quotes needing a rep's attention today: accepted-but-unpaid
 *  (still SENT, still amendable) and/or a customer-requested refresh from the
 *  expired public page. Most urgent first — furthest past expiry (most
 *  negative `daysToExpiry`) sorts first, then closest to expiry, then rows
 *  with no usable `sentAt` last. */
export const buildAcceptedQuoteRows = (
  quotes: QuoteListLike[],
  now: number,
): AcceptedQuoteRow[] => {
  const rows: AcceptedQuoteRow[] = [];

  for (const q of quotes) {
    const accepted = isAcceptedAwaitingDeposit(q);
    const updateRequested = hasRequestedUpdate(q);
    if (!accepted && !updateRequested) continue;

    let daysToExpiry: number | null = null;
    const sentAt = quotationDate(q);
    if (sentAt) {
      const sentMs = new Date(sentAt).getTime();
      if (Number.isFinite(sentMs)) {
        daysToExpiry = Math.ceil((sentMs + THIRTY_DAYS_MS - now) / DAY_MS);
      }
    }

    rows.push({
      quoteId: q.id,
      displayRef: displayQuoteRef(q),
      customerName: q.customer?.name || 'Unnamed customer',
      grandTotal: q.totals?.grandTotal ?? 0,
      signal: accepted && updateRequested ? 'BOTH' : accepted ? 'ACCEPTED' : 'UPDATE_REQUESTED',
      daysToExpiry,
    });
  }

  return rows.sort((a, b) => {
    if (a.daysToExpiry === null && b.daysToExpiry === null) return 0;
    if (a.daysToExpiry === null) return 1;
    if (b.daysToExpiry === null) return -1;
    return a.daysToExpiry - b.daysToExpiry;
  });
};
